// Column Catalog - Single source of truth for all Product Master fields
// Provides SAP/Airtable-style column management with RBAC, formatting, and persistence

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { canViewField, getFieldCategory, getFieldAccessLevel, getRestrictedFieldMessage } from '../../utils/rbac';
import TotalCostCell from '../../components/ui/TotalCostCell';

// CRITICAL FIX: Smart accessor helper to handle nested data and fallbacks
const safeAccess = (row, ...paths) => {
  for (const path of paths) {
    if (typeof path === 'string') {
      // Handle dot notation: 'general.category'
      const keys = path.split('.');
      let value = row;
      let found = true;
      
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          found = false;
          break;
        }
      }
      
      if (found && value !== null && value !== undefined) {
        return value;
      }
    } else if (typeof path === 'function') {
      // Handle function: () => row.field
      try {
        const result = path(row);
        if (result !== null && result !== undefined) {
          return result;
        }
      } catch (e) {
        // Continue to next path
      }
    }
  }
  
  return null; // Return null if all paths fail
};

// Column Metadata Type Definition
export const COLUMN_TYPES = {
  TEXT: 'text',
  NUMBER: 'number', 
  MONEY: 'money',
  PERCENT: 'percent',
  UOM: 'uom',
  DATE: 'date',
  BADGE: 'badge',
  CALC: 'calc'
};

export const SECTIONS = {
  GENERAL: 'General',
  PURCHASING: 'Purchasing', 
  PLANNING: 'Planning',
  SALES: 'Sales',
  STORAGE: 'Storage',
  SHELF_LIFE: 'Shelf Life',
  PACKAGING: 'Packaging',
  PRICING: 'Pricing'
};

// Formatters for different column types
export const formatters = {
  money: (value) => value != null ? `$${Number(value).toFixed(2)}` : <span className="text-orange-600 text-xs">Missing</span>,
  percent: (value) => value != null ? `${Number(value).toFixed(1)}%` : <span className="text-orange-600 text-xs">Missing</span>,
  number: (value) => value != null ? Number(value).toLocaleString() : <span className="text-orange-600 text-xs">Missing</span>,
  badge: (value, options) => {
    if (!value && !options?.fallback) return <span className="text-gray-400 text-xs">None</span>;
    const displayValue = value || options?.fallback || 'Unknown';
    const variant = options?.variant || 'secondary';
    return <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${variant}-100 text-${variant}-800`}>{displayValue}</span>;
  },
  calc: (value, row, deps) => {
    // Check if all dependencies are present
    const missingDeps = deps?.filter(dep => !row[dep]) || [];
    if (missingDeps.length > 0) {
      return <span className="text-orange-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Incomplete</span>;
    }
    return value != null ? `$${Number(value).toFixed(2)}` : <span className="text-orange-600 text-xs">Missing</span>;
  }
};

// Comprehensive Product Master Column Catalog
export const PRODUCT_COLUMN_CATALOG = [
  // === GENERAL SECTION ===
  {
    id: 'general.sku',
    label: 'SKU',
    section: SECTIONS.GENERAL,
    accessor: (row) => row.sku,
    type: COLUMN_TYPES.TEXT,
    width: 120,
    editable: true,
    rbac: ['product.read'],
    pinned: 'left',
    formatter: (value) => <span className="font-mono font-medium">{value}</span>
  },
  {
    id: 'general.name', 
    label: 'Product Name',
    section: SECTIONS.GENERAL,
    accessor: (row) => row.name || row.title,
    type: COLUMN_TYPES.TEXT,
    width: 200,
    editable: false,  // Product Name should only be edited via Edit Details modal
    rbac: ['product.read'],
    pinned: 'left'
  },
  {
    id: 'general.category',
    label: 'Category', 
    section: SECTIONS.GENERAL,
    accessor: (row) => {
      // CRITICAL DEBUG: Log ALL available fields for first row
      if (row.sku === '4622' || row.sku === '4758') {
        console.log('🔍 CATEGORY DEBUG - SKU:', row.sku);
        console.log('🔍 All row keys:', Object.keys(row));
        console.log('🔍 row.category:', row.category);
        console.log('🔍 row.category_id:', row.category_id);
        console.log('🔍 row.category_name:', row.category_name);
        console.log('🔍 row.general:', row.general);
        console.log('🔍 Full row object:', row);
      }
      
      // Try reading from category_id first
      const value = row.category_id || row.category_name || row.category || 'Unknown';
      
      if (row.sku === '4622' || row.sku === '4758') {
        console.log('🎯 RETURNING VALUE:', value);
      }
      
      return value;
    },
    type: COLUMN_TYPES.BADGE,
    width: 140,
    editable: true,
    rbac: ['product.read']
  },
  {
    id: 'general.subcategory',
    label: 'Subcategory',
    section: SECTIONS.GENERAL, 
    // CRITICAL FIX: Read from subcategory_id (database field), NOT subcategory
    accessor: (row) => row.subcategory_id || row.subcategory || safeAccess(row, 'subcategory', 'general.subcategory') || '',
    type: COLUMN_TYPES.TEXT,
    width: 120,
    editable: true,
    rbac: ['product.read'],
    hiddenByDefault: true
  },
  {
    id: 'general.status',
    label: 'Status',
    section: SECTIONS.GENERAL,
    accessor: (row) => safeAccess(row, 'status', 'general.status') || 'ACTIVE',
    type: COLUMN_TYPES.BADGE,
    width: 100,
    editable: true,
    rbac: ['product.read'],
    formatter: (value) => formatters.badge(value, { 
      variant: value === 'ACTIVE' ? 'green' : value === 'ARCHIVED' ? 'red' : 'gray' 
    })
  },
  // ============================================================
  // COMPONENT-BASED INVENTORY: Product Type Column
  // ============================================================
  {
    id: 'inventory.product_type',
    label: 'Type',
    section: SECTIONS.GENERAL,
    accessor: (row) => row.product_type || 'standard',
    type: COLUMN_TYPES.BADGE,
    width: 95,
    editable: false,
    rbac: ['product.read'],
    priority: 3,  // High priority to show earlier
    formatter: (value) => {
      const badges = {
        'raw_material': { label: '🧱 Raw', bgColor: 'bg-orange-500/20', textColor: 'text-orange-400', borderColor: 'border-orange-500/30' },
        'finished_product': { label: '🎁 Fini', bgColor: 'bg-green-500/20', textColor: 'text-green-400', borderColor: 'border-green-500/30' },
        'standard': { label: '📦 Std', bgColor: 'bg-gray-500/20', textColor: 'text-gray-400', borderColor: 'border-gray-500/30' }
      };
      const badge = badges[value] || badges.standard;
      return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border ${badge.bgColor} ${badge.textColor} ${badge.borderColor}`}>
          {badge.label}
        </span>
      );
    }
  },
  // ============================================================
  // COMPONENT-BASED INVENTORY: Uses Column (shows component relationship)
  // ============================================================
  {
    id: 'inventory.uses',
    label: 'Uses',
    section: SECTIONS.GENERAL,
    accessor: (row) => {
      const productType = row.product_type || 'standard';
      if (productType === 'finished_product' && row.component) {
        const componentSku = row.component.sku || row.component;
        const qtyPerUnit = row.component.qty_per_unit || '?';
        return `${componentSku}|${qtyPerUnit}`;
      } else if (productType === 'raw_material') {
        // For raw materials, show child count if available
        return `source|${row.child_count || 0}`;
      }
      return null;
    },
    type: COLUMN_TYPES.TEXT,
    width: 120,
    editable: false,
    rbac: ['product.read'],
    priority: 3.5,  // Between Type and Available
    formatter: (value, row) => {
      const productType = row.product_type || 'standard';
      
      if (productType === 'finished_product' && row.component) {
        const componentSku = row.component.sku || row.component;
        const qtyPerUnit = row.component.qty_per_unit || '?';
        const componentStock = row.component_stock;
        
        return (
          <div 
            className="flex flex-col cursor-help" 
            title={`Uses ${qtyPerUnit} pieces of ${componentSku}\nRaw stock: ${componentStock || 0} pieces\nAvailable: ${row.available_to_sell || 0} packs`}
          >
            <span className="text-blue-400 text-[11px] font-medium">
              {componentSku}
            </span>
            <span className="text-gray-500 text-[10px]">
              {qtyPerUnit} pcs/unit
            </span>
          </div>
        );
      } else if (productType === 'raw_material') {
        const childCount = row.child_count || 0;
        return (
          <span className="text-orange-400 text-[10px]" title={`Used by ${childCount} finished goods`}>
            🧱 {childCount > 0 ? `${childCount} products` : 'Source'}
          </span>
        );
      } else {
        return <span className="text-gray-600 text-[11px]">—</span>;
      }
    }
  },
  // ============================================================
  // COMPONENT-BASED INVENTORY: Available to Sell Column
  // ============================================================
  {
    id: 'inventory.available_to_sell',
    label: 'Available',
    section: SECTIONS.GENERAL,
    accessor: (row) => {
      const productType = row.product_type || 'standard';
      
      if (productType === 'raw_material') {
        return row.current_stock || 0;
      } else if (productType === 'finished_product') {
        if (row.available_to_sell !== undefined) return row.available_to_sell;
        if (row.component && row.component_stock !== undefined) {
          const qtyPerUnit = row.component.qty_per_unit || 1;
          return Math.floor(row.component_stock / qtyPerUnit);
        }
        return 0;
      } else {
        return row.current_stock || row.on_hand || 0;
      }
    },
    type: COLUMN_TYPES.NUMBER,
    width: 100,
    editable: false,
    rbac: ['product.read'],
    priority: 4,  // High priority to show earlier
    headerClass: 'text-blue-400 font-semibold',
    formatter: (value, row) => {
      const productType = row.product_type || 'standard';
      const stock = value || 0;
      
      const getStockColor = (s) => {
        if (s === 0) return 'text-red-400';
        if (s < 10) return 'text-yellow-400';
        return 'text-green-400';
      };
      
      if (productType === 'raw_material') {
        const unit = row.unit || 'pcs';
        return (
          <div className="flex flex-col items-end text-[11px]">
            <span className={`font-medium ${getStockColor(stock)}`}>{stock} {unit}</span>
          </div>
        );
      } else if (productType === 'finished_product') {
        return (
          <div className="flex flex-col items-end text-[11px]">
            <span className={`font-medium ${getStockColor(stock)}`}>{stock} packs</span>
          </div>
        );
      } else {
        return (
          <span className={`font-medium text-[11px] ${getStockColor(stock)}`}>{stock}</span>
        );
      }
    }
  },
  {
    id: 'general.upc',
    label: 'UPC Code', 
    section: SECTIONS.GENERAL,
    accessor: (row) => safeAccess(row, 'upc', 'barcode', 'general.upc'),
    type: COLUMN_TYPES.TEXT,
    width: 120,
    editable: true,
    rbac: ['product.read'],
    hiddenByDefault: true
  },
  {
    id: 'general.brand',
    label: 'Brand',
    section: SECTIONS.GENERAL,
    accessor: (row) => safeAccess(row, 'brand', 'general.brand', 'brand_name'),
    type: COLUMN_TYPES.TEXT, 
    width: 100,
    editable: true,
    rbac: ['product.read'],
    hiddenByDefault: true
  },
  {
    id: 'general.description',
    label: 'Description',
    section: SECTIONS.GENERAL,
    accessor: (row) => safeAccess(row, 'description', 'general.description', 'product_description'),
    type: COLUMN_TYPES.TEXT,
    width: 200,
    editable: true,
    rbac: ['product.read'],
    hiddenByDefault: true
  },

  // === PURCHASING SECTION ===
  {
    id: 'purch.orderingUom',
    label: 'Ordering UoM',
    section: SECTIONS.PURCHASING,
    accessor: (row) => safeAccess(row, 'ordering_uom', 'unit_of_measure', 'purchasing.ordering_uom', 'purchase_uom') || 'PCS',
    type: COLUMN_TYPES.UOM,
    width: 100,
    editable: true,
    rbac: ['product.read', 'purchasing.read']
  },
  {
    id: 'purch.leadTimeDays', 
    label: 'Lead Time (Days)',
    section: SECTIONS.PURCHASING,
    accessor: (row) => safeAccess(row, 'gr_processing_time_days', 'lead_time_days', 'purchasing.lead_time_days', 'lead_time'),
    type: COLUMN_TYPES.NUMBER,
    width: 120,
    editable: true,
    rbac: ['product.read', 'purchasing.read']
  },
  {
    id: 'purch.supplierPrice',
    label: 'Supplier Price',
    section: SECTIONS.PURCHASING, 
    accessor: (row) => safeAccess(row, 'supplier_price', 'purchasing.supplier_price', 'last_purchase_price', 'purchase_price'),
    type: COLUMN_TYPES.MONEY,
    width: 120,
    editable: true,
    rbac: ['product.read', 'purchasing.read', 'finance.read'],
    formatter: formatters.money
  },
  {
    id: 'purch.supplierName',
    label: 'Supplier',
    section: SECTIONS.PURCHASING,
    accessor: (row) => safeAccess(row, 'supplier_name', 'supplier', 'purchasing.supplier_name', 'vendor'),
    type: COLUMN_TYPES.TEXT,
    width: 150,
    editable: true,
    rbac: ['product.read', 'purchasing.read'],
    hiddenByDefault: true
  },

  // === PLANNING SECTION ===
  {
    id: 'plan.reorderPoint',
    label: 'Reorder Point',
    section: SECTIONS.PLANNING,
    // CRITICAL FIX: Read from inventory.reorder_point first (new consolidated structure), fallback to old fields
    accessor: (row) => safeAccess(row, 'inventory.reorder_point', 'reorder_point', 'planning.reorder_point', 'min_stock') || 0,
    type: COLUMN_TYPES.NUMBER,
    width: 110,
    editable: true,
    rbac: ['product.read', 'planning.read']
  },
  {
    id: 'plan.targetStock',
    label: 'Target Stock',
    section: SECTIONS.PLANNING,
    // CRITICAL FIX: Read from inventory.target_stock_level first (new consolidated structure), fallback to old fields
    accessor: (row) => safeAccess(row, 'inventory.target_stock_level', 'target_stock_level', 'target_stock', 'planning.target_stock', 'max_stock') || 0,
    type: COLUMN_TYPES.NUMBER, 
    width: 110,
    editable: true,
    rbac: ['product.read', 'planning.read']
  },
  {
    id: 'plan.onHand',
    label: 'Current Stock', // Renamed from "On Hand" to match Inventory tab
    section: SECTIONS.PLANNING,
    // CRITICAL DEBUG: Added extensive logging to diagnose Smart Grid display issue
    accessor: (row) => {
      // DEBUG LOGGING - See what data the accessor receives
      console.log('🔍 CURRENT STOCK ACCESSOR DEBUG:', {
        sku: row.sku || row.id,
        hasInventoryKey: 'inventory' in row,
        'row.inventory': row.inventory,
        'row.inventory?.current_stock': row.inventory?.current_stock,
        'row.current_stock': row.current_stock,
        hasPlanningKey: 'planning' in row,
        'row.planning': row.planning,
        'row.planning?.on_hand': row.planning?.on_hand,
        'row.on_hand': row.on_hand,
        allRowKeys: Object.keys(row).slice(0, 20) // Show first 20 keys
      });
      
      // Try direct access first (bypass safeAccess helper)
      let value = null;
      
      if (row.inventory && typeof row.inventory === 'object' && 'current_stock' in row.inventory) {
        value = row.inventory.current_stock;
        console.log('✅ Found value in row.inventory.current_stock:', value);
      } else if ('current_stock' in row) {
        value = row.current_stock;
        console.log('✅ Found value in row.current_stock:', value);
      } else if (row.planning && typeof row.planning === 'object' && 'on_hand' in row.planning) {
        value = row.planning.on_hand;
        console.log('✅ Found value in row.planning.on_hand:', value);
      } else if ('on_hand' in row) {
        value = row.on_hand;
        console.log('✅ Found value in row.on_hand:', value);
      } else {
        value = 0;
        console.log('❌ NO VALUE FOUND - Defaulting to 0');
      }
      
      console.log('🔍 RETURNED VALUE:', value);
      return value || 0;
    },
    type: COLUMN_TYPES.NUMBER,
    width: 100,
    editable: false,
    rbac: ['product.read', 'inventory.read'],
    formatter: (value, row) => {
      // CRITICAL DEBUG: Simplified formatter for debugging
      console.log('🎨 CURRENT STOCK FORMATTER - SKU:', row?.sku, 'VALUE:', value);
      
      // TEMP: Return plain string to test if React rendering is the issue
      return String(value || 0);
    }
  },
  {
    id: 'plan.reorderStatus',
    label: 'Reorder Status',
    section: SECTIONS.PLANNING,
    accessor: (row) => {
      // CRITICAL DEBUG: Added logging to diagnose reorder status calculation
      console.log('🔍 REORDER STATUS ACCESSOR DEBUG:', {
        sku: row.sku || row.id,
        'row.inventory': row.inventory,
        'row.inventory?.current_stock': row.inventory?.current_stock,
        'row.inventory?.reorder_point': row.inventory?.reorder_point,
        'row.current_stock': row.current_stock,
        'row.reorder_point': row.reorder_point
      });
      
      // Try direct access
      const stock = row.inventory?.current_stock ?? row.current_stock ?? row.planning?.on_hand ?? row.on_hand ?? 0;
      const reorderPoint = row.inventory?.reorder_point ?? row.reorder_point ?? row.planning?.reorder_point ?? 10;
      
      console.log('🔍 REORDER STATUS CALCULATION:', { stock, reorderPoint });
      
      let status;
      if (stock === 0) {
        status = 'OUT_OF_STOCK';
      } else if (stock <= reorderPoint) {
        status = 'REORDER_NEEDED';
      } else {
        status = 'HEALTHY';
      }
      
      console.log('🔍 REORDER STATUS RESULT:', status);
      return status;
    },
    type: COLUMN_TYPES.CALC,
    width: 150,
    editable: false,
    rbac: ['product.read', 'inventory.read'],
    calcDeps: ['inventory.current_stock', 'inventory.reorder_point'],
    formatter: (value) => {
      const badges = {
        'OUT_OF_STOCK': { text: '🔴 OUT OF STOCK', className: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-700' },
        'REORDER_NEEDED': { text: '⚠️ REORDER NEEDED', className: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border border-yellow-300 dark:border-yellow-700' },
        'HEALTHY': { text: '✅ HEALTHY', className: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-300 dark:border-green-700' }
      };
      const badge = badges[value] || badges['HEALTHY'];
      return <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium ${badge.className}`}>{badge.text}</span>;
    }
  },


  // === SALES SECTION ===
  {
    id: 'sales.sellingUom',
    label: 'Selling UoM', 
    section: SECTIONS.SALES,
    accessor: (row) => safeAccess(row, 'selling_uom', 'sales.selling_uom', 'unit_of_measure', 'sale_uom') || 'PCS',
    type: COLUMN_TYPES.UOM,
    width: 100,
    editable: true,
    rbac: ['product.read', 'sales.read']
  },
  {
    id: 'sales.defaultPrice',
    label: 'Selling Price',
    section: SECTIONS.SALES,
    accessor: (row) => row.price || row.default_price,
    type: COLUMN_TYPES.MONEY,
    width: 110,
    editable: true,
    rbac: ['product.read', 'sales.read', 'finance.read'],
    formatter: formatters.money
  },
  {
    id: 'sales.channel',
    label: 'Channel',
    section: SECTIONS.SALES,
    accessor: (row) => row.channel || 'shopify',
    type: COLUMN_TYPES.BADGE,
    width: 100,
    editable: true,
    rbac: ['product.read', 'sales.read'],
    hiddenByDefault: true
  },

  // === STORAGE SECTION ===  
  {
    id: 'storage.location',
    label: 'Warehouse Location',
    section: SECTIONS.STORAGE,
    // CRITICAL FIX: Read from inventory.storage_location first (new consolidated structure), fallback to old fields
    accessor: (row) => safeAccess(row, 'inventory.storage_location', 'storage_location', 'warehouse_location', 'bin_location', 'storage.warehouse_location', 'location') || '',
    type: COLUMN_TYPES.TEXT,
    width: 140,
    editable: true,
    rbac: ['product.read', 'warehouse.read'],
    hiddenByDefault: true
  },
  {
    id: 'storage.condition',
    label: 'Storage Condition',
    section: SECTIONS.STORAGE,
    // CRITICAL FIX: Read from inventory.storage_condition first (new consolidated structure), fallback to old fields
    accessor: (row) => safeAccess(row, 'inventory.storage_condition', 'storage_condition', 'storage.storage_condition', 'condition') || 'Room Temperature',
    type: COLUMN_TYPES.BADGE,
    width: 130,
    editable: true,
    rbac: ['product.read', 'warehouse.read'],
    hiddenByDefault: true,
    formatter: (value) => formatters.badge(value, { fallback: 'Room Temperature' })
  },
  {
    id: 'storage.warehouseCode',
    label: 'Warehouse Code',
    section: SECTIONS.STORAGE,
    // CRITICAL FIX: Read from inventory.warehouse_code first (new consolidated structure), fallback to old fields
    accessor: (row) => safeAccess(row, 'inventory.warehouse_code', 'warehouse_code', 'storage.warehouse_code') || '',
    type: COLUMN_TYPES.TEXT,
    width: 130,
    editable: true,
    rbac: ['product.read', 'warehouse.read'],
    hiddenByDefault: true
  },


  // === SHELF LIFE SECTION ===
  {
    id: 'shelfLife.days',
    label: 'Shelf Life (Days)',
    section: SECTIONS.SHELF_LIFE,
    accessor: (row) => safeAccess(row, 'shelf_life_days', 'shelfLife.days', 'shelf_life'),
    type: COLUMN_TYPES.NUMBER,
    width: 130,
    editable: true,
    rbac: ['product.read', 'quality.read'],
    hiddenByDefault: true
  },
  {
    id: 'shelfLife.batchNo',
    label: 'Batch Number',
    section: SECTIONS.SHELF_LIFE,
    accessor: (row) => safeAccess(row, 'batch_no', 'lot_number', 'shelfLife.batch_no', 'batch_number'),
    type: COLUMN_TYPES.TEXT,
    width: 120,
    editable: true,
    rbac: ['product.read', 'quality.read'],
    hiddenByDefault: true
  },
  {
    id: 'shelfLife.expirySoon',
    label: 'Expiry Status',
    section: SECTIONS.SHELF_LIFE,
    accessor: (row) => {
      // Mock calculation - would be real expiry logic
      const shelfLife = safeAccess(row, 'shelf_life_days', 'shelfLife.days') || 365;
      return shelfLife < 30 ? 'Expiring Soon' : shelfLife < 90 ? 'Monitor' : 'Good';
    },
    type: COLUMN_TYPES.CALC,
    width: 110,
    editable: false,
    rbac: ['product.read', 'quality.read'],
    hiddenByDefault: true,
    calcDeps: ['shelf_life_days'],
    formatter: (value) => formatters.badge(value, { 
      variant: value === 'Expiring Soon' ? 'red' : value === 'Monitor' ? 'orange' : 'green' 
    })
  },

  // === PACKAGING SECTION ===
  {
    id: 'pack.casePackQty',
    label: 'Case Pack Qty',
    section: SECTIONS.PACKAGING,
    accessor: (row) => safeAccess(row, 'case_pack_qty', 'packaging.case_pack_qty', 'casePack.units_per_case', 'units_per_package'),
    type: COLUMN_TYPES.NUMBER,
    width: 120,
    editable: true,
    rbac: ['product.read', 'packaging.read'],
    hiddenByDefault: true
  },
  {
    id: 'pack.unitsPerPackage',
    label: 'Units/Package',
    section: SECTIONS.PACKAGING,
    // CRITICAL FIX: Read from inventory.units_per_package first (new consolidated structure), fallback to old fields
    accessor: (row) => safeAccess(row, 'inventory.units_per_package', 'units_per_package', 'packaging.units_per_package') || 1,
    type: COLUMN_TYPES.NUMBER,
    width: 120,
    editable: true,
    rbac: ['product.read', 'packaging.read'],
    hiddenByDefault: true
  },
  {
    id: 'pack.weight',
    label: 'Weight',
    section: SECTIONS.PACKAGING,
    // CRITICAL FIX: Read weight and unit from inventory structure (imperial by default)
    accessor: (row) => {
      const weight = safeAccess(row, 'inventory.weight', 'weight', 'weight_kg', 'packaging.weight_kg') || 0;
      const unit = safeAccess(row, 'inventory.weight_unit', 'weight_unit') || 'lb';
      return { value: weight, unit: unit };
    },
    type: COLUMN_TYPES.TEXT,
    width: 120,
    editable: true,
    rbac: ['product.read', 'packaging.read'],
    hiddenByDefault: true,
    formatter: (data) => {
      if (!data || data.value === 0) return '-';
      let weight = Number(data.value);
      const unit = data.unit || 'lb';
      
      // FIXED: Always display in LB (convert OZ to LB if needed)
      if (unit === 'oz') {
        // Convert oz to lb: oz ÷ 16 = lb
        weight = weight / 16;
        return `${weight.toFixed(2)} lb`;
      } else if (unit === 'kg') {
        // Convert kg to lb: kg × 2.20462 = lb
        weight = weight * 2.20462;
        return `${weight.toFixed(2)} lb`;
      } else {
        // Already in lb, display as-is
        return `${weight.toFixed(2)} lb`;
      }
    }
  },

  {
    id: 'pack.unitGrossWeight',
    label: 'Unit Gross Wt (g)',
    section: SECTIONS.PACKAGING,
    accessor: (row) => safeAccess(row, 'unit_gross_weight', 'packaging.unit_gross_weight', 'gross_weight', 'weight_kg'),
    type: COLUMN_TYPES.NUMBER,
    width: 130,
    editable: true,
    rbac: ['product.read', 'packaging.read'],
    hiddenByDefault: true
  },
  {
    id: 'pack.unitNetWeight',
    label: 'Unit Net Wt (g)',
    section: SECTIONS.PACKAGING,
    accessor: (row) => safeAccess(row, 'unit_net_weight', 'packaging.unit_net_weight', 'net_weight', 'net_weight_per_unit_g'),
    type: COLUMN_TYPES.NUMBER,
    width: 120,
    editable: true,
    rbac: ['product.read', 'packaging.read'],
    hiddenByDefault: true
  },
  {
    id: 'pack.unitDims',
    label: 'Unit Dimensions',
    section: SECTIONS.PACKAGING,
    accessor: (row) => row.unit_dimensions || `${row.unit_length || 0} x ${row.unit_width || 0} x ${row.unit_height || 0}`,
    type: COLUMN_TYPES.TEXT,
    width: 130,
    editable: true,
    rbac: ['product.read', 'packaging.read'],
    hiddenByDefault: true
  },

  // === PRICING SECTION (from pricing_components table) ===
  {
    id: 'price.cogm',
    label: 'COGM',
    section: SECTIONS.PRICING,
    accessor: (row) => row.cogm,
    type: COLUMN_TYPES.MONEY,
    width: 100,
    editable: true,
    rbac: ['product.read', 'finance.read'],
    hiddenByDefault: true,
    formatter: formatters.money
  },
  {
    id: 'price.totalLandedCost',
    label: 'Total Landed Cost',
    section: SECTIONS.PRICING,
    accessor: (row) => row.total_landed_cost,
    type: COLUMN_TYPES.CALC,
    width: 140,
    editable: false,
    rbac: ['product.read', 'finance.read'],
    calcDeps: ['cogm', 'intl_freight', 'import_duties', 'inbound_handling', 'domestic_freight'],
    formatter: formatters.calc
  },
  {
    id: 'price.effectiveCost',
    label: 'Effective Cost',
    section: SECTIONS.PRICING,
    accessor: (row) => row.effective_cost,
    type: COLUMN_TYPES.MONEY,
    width: 120,
    editable: false,
    rbac: ['product.read', 'finance.read'],
    formatter: formatters.money
  },
  {
    id: 'price.cogs',
    label: 'COGS',
    section: SECTIONS.PRICING,
    accessor: (row) => row.cogs,
    type: COLUMN_TYPES.CALC,
    width: 100,
    editable: false,
    rbac: ['product.read', 'finance.read'],
    calcDeps: ['total_landed_cost', 'fulfillment_fee', 'warehousing_cost', 'outbound_freight'],
    formatter: formatters.calc
  },
  {
    id: 'price.grossProfit',
    label: 'Gross Profit',
    section: SECTIONS.PRICING,
    accessor: (row) => row.gross_profit,
    type: COLUMN_TYPES.CALC,
    width: 110,
    editable: false,
    rbac: ['product.read', 'finance.read'],
    calcDeps: ['price', 'cogs'],
    formatter: formatters.calc
  },
  {
    id: 'price.grossMarginPct',
    label: 'Gross Margin %',
    section: SECTIONS.PRICING,
    accessor: (row) => row.gross_margin_pct,
    type: COLUMN_TYPES.CALC,
    width: 120,
    editable: false,
    rbac: ['product.read', 'finance.read'],
    calcDeps: ['gross_profit', 'price'],
    formatter: (value, row) => {
      if (value == null) return formatters.calc(null, row, ['gross_profit', 'price']);
      const marginValue = Number(value);
      
      // Margin health indicators: green >40%, amber 25-40%, red <25%
      let badgeClass = '';
      if (marginValue >= 40) {
        badgeClass = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      } else if (marginValue >= 25) {
        badgeClass = 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300';
      } else {
        badgeClass = 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      }
      
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
          {marginValue.toFixed(1)}%
        </span>
      );
    }
  },
  {
    id: 'price.netMarginPct',
    label: 'Net Margin %',
    section: SECTIONS.PRICING,
    accessor: (row) => row.net_margin_pct,
    type: COLUMN_TYPES.CALC,
    width: 110,
    editable: false,
    rbac: ['product.read', 'finance.read'],
    calcDeps: ['gross_margin_pct', 'ad_rate_pct'],
    formatter: (value, row) => {
      if (value == null) return formatters.calc(null, row, ['gross_margin_pct', 'ad_rate_pct']);
      const marginValue = Number(value);
      
      // Margin health indicators: green >40%, amber 25-40%, red <25%
      let badgeClass = '';
      if (marginValue >= 40) {
        badgeClass = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      } else if (marginValue >= 25) {
        badgeClass = 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300';
      } else {
        badgeClass = 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      }
      
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
          {marginValue.toFixed(1)}%
        </span>
      );
    }
  },
  {
    id: 'price.profitAtPrice',
    label: 'Profit @ Price',
    section: SECTIONS.PRICING,
    accessor: (row) => {
      const price = row.price || row.default_price || 0;
      const effectiveCost = row.effective_cost || row.cogs || 0;
      return price && effectiveCost ? price - effectiveCost : null;
    },
    type: COLUMN_TYPES.CALC,
    width: 110,
    editable: false,
    rbac: ['product.read', 'finance.read'],
    calcDeps: ['price', 'effective_cost'],
    formatter: (value, row) => {
      if (value === null || value === undefined) return formatters.calc(null, row, ['price', 'effective_cost']);
      const className = value < 0 ? 'text-red-600' : value > 0 ? 'text-green-600' : 'text-gray-600';
      return <span className={className}>${Number(value).toFixed(2)}</span>;
    }
  },
  
  // === ENHANCED PRICING COLUMNS (Aligned with Zuperbooks Structure) ===
  {
    id: 'price.fulfillmentTotal',
    label: 'Fulfillment Total',
    section: SECTIONS.PRICING,
    accessor: (row) => {
      const fulfillment = Number(row.fulfillment_fee || 0);
      const storage = Number(row.warehousing_cost || 0);
      const transport = Number(row.outbound_freight || 0);
      return fulfillment + storage + transport;
    },
    type: COLUMN_TYPES.CALC,
    width: 130,
    editable: false,
    rbac: ['product.read', 'finance.read'],
    calcDeps: ['fulfillment_fee', 'warehousing_cost', 'outbound_freight'],
    hiddenByDefault: true,
    formatter: formatters.calc
  },
  {
    id: 'price.adSpend',
    label: 'Ad Spend (IDS)',
    section: SECTIONS.PRICING,
    accessor: (row) => row.advertising_expense || row.ad_spend_allocation || 0,
    type: COLUMN_TYPES.MONEY,
    width: 120,
    editable: false,
    rbac: ['product.read', 'finance.read'],
    hiddenByDefault: true,
    formatter: formatters.money
  },
  {
    id: 'price.totalCOGS',
    label: 'Total COGS',
    section: SECTIONS.PRICING,
    accessor: (row) => row.total_cogs || 0,
    type: COLUMN_TYPES.CALC,
    width: 110,
    editable: false,
    rbac: ['product.read', 'finance.read'],
    calcDeps: ['total_landed_cost', 'fulfillment_fee', 'warehousing_cost', 'outbound_freight', 'advertising_expense'],
    formatter: (value, row) => {
      if (value == null || value === 0) {
        // Check if dependencies exist
        const deps = ['total_landed_cost', 'fulfillment_fee', 'warehousing_cost', 'outbound_freight', 'advertising_expense'];
        const missingDeps = deps.filter(dep => !row[dep]);
        if (missingDeps.length > 0) {
          return <span className="text-orange-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Incomplete</span>;
        }
      }
      return <span className="font-semibold text-blue-600">${Number(value).toFixed(2)}</span>;
    }
  },
  {
    id: 'price.sellingPrice',
    label: 'Selling Price',
    section: SECTIONS.PRICING,
    accessor: (row) => row.selling_price || row.price || row.default_price || 0,
    type: COLUMN_TYPES.MONEY,
    width: 110,
    editable: true,
    rbac: ['product.read', 'product.write'],
    formatter: (value) => {
      if (value == null || value === 0) {
        return <span className="text-gray-400 text-xs">Not set</span>;
      }
      return <span className="font-semibold text-green-600">${Number(value).toFixed(2)}</span>;
    }
  },
  {
    id: 'price.pricingMode',
    label: 'Pricing Mode',
    section: SECTIONS.PRICING,
    accessor: (row) => row.pricing_mode || 'manual',
    type: COLUMN_TYPES.BADGE,
    width: 110,
    editable: false,
    rbac: ['product.read'],
    hiddenByDefault: true,
    formatter: (value) => {
      const isAllocation = value === 'allocation';
      const badgeClass = isAllocation 
        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
        : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
      const icon = isAllocation ? '📦' : '✏️';
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
          {icon} {isAllocation ? 'Allocation' : 'Manual'}
        </span>
      );
    }
  },

  // === PHASE 1: PRODUCT PRICING & COST ALLOCATION COLUMNS ===
  {
    id: 'pricing.totalCost',
    label: 'Total Cost',
    section: SECTIONS.PRICING,
    accessor: (row) => row.total_cost || row.pricing_total_cost,
    type: COLUMN_TYPES.MONEY,
    width: 120,
    editable: false,
    rbac: ['finance.read', 'product.read'],
    tooltip: 'Sum of all cost components (manufacturing + freight + duties + handling + overhead + ad spend + fulfillment)',
    formatter: (value, row, context) => {
      const { onOpenPricingDrawer } = context || {};
      const cost = value || 0;
      return (
        <div 
          className="cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded px-2 py-1 transition-colors"
          onClick={() => onOpenPricingDrawer && onOpenPricingDrawer(row.sku)}
          title="Click to open pricing drawer"
        >
          <TotalCostCell
            cost={cost}
            row={row}
          />
        </div>
      );
    }
  },
  {
    id: 'pricing.targetMargin',
    label: 'Target Margin %',
    section: SECTIONS.PRICING,
    accessor: (row) => row.target_margin_percent || row.pricing_target_margin,
    type: COLUMN_TYPES.PERCENT,
    width: 140,
    editable: true,
    rbac: ['finance.edit', 'ownerops.edit'],
    tooltip: 'Target profit margin percentage for pricing calculations',
    validation: (value) => value >= 0 && value <= 100,
    formatter: (value, row, context) => {
      const { onPricingUpdate, hasEditPermission, onOpenPricingDrawer } = context || {};
      
      // Debug logs removed to prevent console spam
      
      // Import EditableCell dynamically to avoid circular imports
      const EditableCell = React.lazy(() => import('../../components/ui/EditableCell.jsx'));
      
      return (
        <React.Suspense fallback={<span className="text-gray-400">Loading...</span>}>
          <EditableCell
            value={value || 40}
            type="percent"
            editable={hasEditPermission && hasEditPermission('finance.edit', 'ownerops.edit')}
            suffix="%"
            placeholder="40.0"
            formatDisplay={(val) => `${Number(val).toFixed(1)}`}
            validation={(val) => val >= 0 && val <= 100}
            className={value >= 40 ? 'text-green-600' : value >= 25 ? 'text-yellow-600' : 'text-red-600'}
            onSave={async (newValue) => {
              if (onPricingUpdate) {
                await onPricingUpdate(row.sku, { 
                  target_margin_percent: newValue,
                  change_reason: `Updated target margin to ${newValue}%`
                });
              }
            }}
          />
        </React.Suspense>
      );
    }
  },
  {
    id: 'pricing.systemPrice',
    label: 'System Price',
    section: SECTIONS.PRICING,
    accessor: (row) => row.system_price || row.pricing_system_price,
    type: COLUMN_TYPES.MONEY,
    width: 130,
    editable: false,
    rbac: ['finance.read', 'product.read'],
    tooltip: 'Auto-calculated price based on total cost and target margin',
    formatter: (value, row) => {
      if (!value) return <span className="text-orange-600 text-xs">Calc Needed</span>;
      return <span className="text-gray-600">${Number(value).toFixed(2)}</span>;
    }
  },
  {
    id: 'pricing.finalPrice',
    label: 'Final Price',
    section: SECTIONS.PRICING,
    accessor: (row) => row.final_price || row.pricing_final_price,
    type: COLUMN_TYPES.MONEY,
    width: 130,
    editable: true,
    rbac: ['finance.edit', 'ownerops.edit'],
    tooltip: 'Final selling price (system calculated or manually overridden)',
    validation: (value) => value > 0,
    formatter: (value, row, context) => {
      const { onPricingUpdate, hasEditPermission, onOpenPricingDrawer } = context || {};
      
      
      const EditableCell = React.lazy(() => import('../../components/ui/EditableCell.jsx'));
      const isManualPricing = row.pricing_mode === 'manual';
      
      return (
        <React.Suspense fallback={<span className="text-gray-400">Loading...</span>}>
          <div className="flex items-center">
            <EditableCell
              value={value || 0}
              type="currency"
              editable={hasEditPermission && hasEditPermission('finance.edit', 'ownerops.edit')}
              prefix="$"
              placeholder="0.00"
              formatDisplay={(val) => Number(val).toFixed(2)}
              validation={(val) => val > 0}
              className={isManualPricing ? 'font-semibold text-blue-600' : 'text-gray-900'}
              onSave={async (newValue) => {
                if (onPricingUpdate) {
                  await onPricingUpdate(row.sku, { 
                    final_price: newValue,
                    pricing_mode: 'manual',
                    change_reason: `Manually set price to $${newValue}`
                  });
                }
              }}
            />
            {isManualPricing && (
              <span className="ml-1 text-xs" title="Manual pricing mode">📝</span>
            )}
          </div>
        </React.Suspense>
      );
    }
  },
  {
    id: 'pricing.netMargin',
    label: 'Net Margin %',
    section: SECTIONS.PRICING,
    accessor: (row) => row.net_margin_percent || row.pricing_net_margin,
    type: COLUMN_TYPES.PERCENT,
    width: 140,
    editable: false,
    rbac: ['finance.read', 'product.read'],
    tooltip: 'Actual profit margin: ((Final Price - Total Cost) ÷ Final Price) × 100',
    formatter: (value, row) => {
      if (!value && value !== 0) return <span className="text-gray-400">0%</span>;
      
      // Margin health indicators with emojis
      let healthClass, emoji;
      if (value >= 40) {
        healthClass = 'text-green-600 font-semibold';
        emoji = '🟢';
      } else if (value >= 25) {
        healthClass = 'text-yellow-600 font-medium';
        emoji = '🟡';
      } else if (value >= 10) {
        healthClass = 'text-orange-600';
        emoji = '🟠';
      } else {
        healthClass = 'text-red-600 font-semibold';
        emoji = '🔴';
      }
      
      return (
        <div className="flex items-center gap-1">
          <span className={healthClass}>{Number(value).toFixed(1)}%</span>
          <span className="text-xs">{emoji}</span>
        </div>
      );
    }
  },

  // === WHOLESALE/RETAIL PRICING COLUMNS (Phase B Integration) ===
  {
    id: 'pricing.wholesalePrice',
    label: 'WHOLESALE PRICE',
    section: SECTIONS.PRICING,
    accessor: (row) => row.wholesale_price || 0,
    type: COLUMN_TYPES.MONEY,
    width: 150,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'product.read'],
    tooltip: 'B2B wholesale pricing (calculated from cost + wholesale margin)',
    headerClass: 'text-blue-400', // Blue for wholesale columns
    formatter: (value, row, context) => {
      const { onOpenPricingDrawer } = context || {};
      const price = value !== undefined && value !== null ? value : null;
      // CRITICAL FIX: Show price if field exists (even if 0), hide only if null/undefined
      const hasPrice = price !== null;
      
      if (!hasPrice) {
        return <span className="text-gray-400">-</span>;
      }

      return (
        <div 
          className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded px-2 py-1 transition-colors"
          onClick={() => onOpenPricingDrawer && onOpenPricingDrawer(row.sku)}
          title="Click to open pricing drawer"
        >
          <span className="font-semibold text-blue-600 dark:text-blue-400">
            ${Number(price).toFixed(2)}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 dark:border-blue-500/30">
            W
          </span>
        </div>
      );
    }
  },
  {
    id: 'pricing.wholesaleMargin',
    label: 'WHOLESALE MARGIN %',
    section: SECTIONS.PRICING,
    accessor: (row) => row.wholesale_margin_percent || 0,
    type: COLUMN_TYPES.PERCENT,
    width: 180,
    sortable: true,
    editable: true,
    rbac: ['finance.edit', 'ownerops.edit'],
    tooltip: 'Target margin for wholesale/B2B sales (typically 15-35%)',
    validation: (value) => value >= 0 && value <= 50,
    headerClass: 'text-blue-400', // Blue for wholesale columns
    formatter: (value, row, context) => {
      const { onWholesaleMarginUpdate, hasEditPermission } = context || {};
      // CRITICAL FIX: Calculate margin from actual prices if not provided
      let marginValue = value || 0;
      if (marginValue === 0 && row.wholesale_price > 0 && row.cost_price > 0) {
        marginValue = ((row.wholesale_price - row.cost_price) / row.wholesale_price) * 100;
      }
      const isActive = row.pricing_tier === 'wholesale' || row.pricing_tier === 'both' || (row.wholesale_price > 0);
      
      if (!isActive && marginValue === 0) {
        return <span className="text-gray-400">-</span>;
      }
      
      const EditableCell = React.lazy(() => import('../../components/ui/EditableCell.jsx'));
      
      return (
        <React.Suspense fallback={<span className="text-gray-400">Loading...</span>}>
          <EditableCell
            value={marginValue}
            type="percent"
            editable={hasEditPermission && hasEditPermission('finance.edit', 'ownerops.edit')}
            suffix="%"
            placeholder="25.0"
            formatDisplay={(val) => `${Number(val).toFixed(1)}`}
            validation={(val) => {
              if (val < 0 || val > 50) return "Wholesale margin must be 0-50%";
              if (val < 15) return "Warning: Low margin (<15%)";
              if (val > 35) return "Warning: High margin (>35%)";
              return null;
            }}
            className={marginValue >= 25 ? 'text-green-600' : marginValue >= 15 ? 'text-yellow-600' : 'text-red-600'}
            onSave={async (newValue) => {
              if (onWholesaleMarginUpdate) {
                await onWholesaleMarginUpdate(row.sku, newValue);
              }
            }}
          />
        </React.Suspense>
      );
    }
  },
  {
    id: 'pricing.retailPrice',
    label: 'RETAIL PRICE',
    section: SECTIONS.PRICING,
    accessor: (row) => row.retail_price || 0,
    type: COLUMN_TYPES.MONEY,
    width: 140,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'product.read'],
    tooltip: 'B2C retail pricing (calculated from cost + retail margin)',
    headerClass: 'text-green-400', // Green for retail columns
    formatter: (value, row, context) => {
      const { onOpenPricingDrawer } = context || {};
      const price = value !== undefined && value !== null ? value : null;
      // CRITICAL FIX: Show price if field exists (even if 0), hide only if null/undefined
      const hasPrice = price !== null;
      
      if (!hasPrice) {
        return <span className="text-gray-400">-</span>;
      }

      return (
        <div 
          className="flex items-center gap-2 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 rounded px-2 py-1 transition-colors"
          onClick={() => onOpenPricingDrawer && onOpenPricingDrawer(row.sku)}
          title="Click to open pricing drawer"
        >
          <span className="font-semibold text-green-600 dark:text-green-400">
            ${Number(price).toFixed(2)}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/30">
            R
          </span>
        </div>
      );
    }
  },
  {
    id: 'pricing.retailMargin',
    label: 'RETAIL MARGIN %',
    section: SECTIONS.PRICING,
    accessor: (row) => row.retail_margin_percent || 0,
    type: COLUMN_TYPES.PERCENT,
    width: 170,
    sortable: true,
    editable: true,
    rbac: ['finance.edit', 'ownerops.edit'],
    tooltip: 'Target margin for retail/B2C sales (typically 35-60%)',
    validation: (value) => value >= 0 && value <= 70,
    headerClass: 'text-green-400', // Green for retail columns
    formatter: (value, row, context) => {
      const { onRetailMarginUpdate, hasEditPermission } = context || {};
      // CRITICAL FIX: Calculate margin from actual prices if not provided
      let marginValue = value || 0;
      if (marginValue === 0 && row.retail_price > 0 && row.cost_price > 0) {
        marginValue = ((row.retail_price - row.cost_price) / row.retail_price) * 100;
      }
      const isActive = row.pricing_tier === 'retail' || row.pricing_tier === 'both' || (row.retail_price > 0);
      
      if (!isActive && marginValue === 0) {
        return <span className="text-gray-400">-</span>;
      }
      
      const EditableCell = React.lazy(() => import('../../components/ui/EditableCell.jsx'));
      
      return (
        <React.Suspense fallback={<span className="text-gray-400">Loading...</span>}>
          <EditableCell
            value={marginValue}
            type="percent"
            editable={hasEditPermission && hasEditPermission('finance.edit', 'ownerops.edit')}
            suffix="%"
            placeholder="45.0"
            formatDisplay={(val) => `${Number(val).toFixed(1)}`}
            validation={(val) => {
              if (val < 0 || val > 70) return "Retail margin must be 0-70%";
              if (val < 35) return "Warning: Low margin (<35%)";
              if (val > 60) return "Warning: High margin (>60%)";
              return null;
            }}
            className={marginValue >= 45 ? 'text-green-600' : marginValue >= 35 ? 'text-yellow-600' : 'text-red-600'}
            onSave={async (newValue) => {
              if (onRetailMarginUpdate) {
                await onRetailMarginUpdate(row.sku, newValue);
              }
            }}
          />
        </React.Suspense>
      );
    }
  },
  {
    id: 'pricing.costPrice',
    label: 'COST PRICE',
    section: SECTIONS.PRICING,
    // CRITICAL FIX: Use pricing_v2.total_cost (complete COGS) as primary source
    // Fallback to cost_price for legacy data
    accessor: (row) => row.pricing_v2?.total_cost || row.cost_price || 0,
    type: COLUMN_TYPES.MONEY,
    width: 140,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'ownerops.read'],
    tooltip: 'Total COGS (manufacturing + shipping + fulfillment + marketing)',
    headerClass: 'text-orange-500 font-semibold', // Orange PROMINENT for cost columns
    formatter: (value, row, context) => {
      const { onOpenPricingDrawer } = context || {};
      // CRITICAL FIX: Use pricing_v2.total_cost as primary, fallback to cost_price
      const price = row.pricing_v2?.total_cost ?? row.cost_price ?? null;
      // CRITICAL FIX: Show price if field exists (even if 0), hide only if null/undefined
      const hasPrice = price !== null;
      
      if (!hasPrice) {
        return <span className="text-gray-400">-</span>;
      }

      return (
        <div 
          className="flex items-center gap-2 cursor-pointer hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded px-2 py-1 transition-colors"
          onClick={() => onOpenPricingDrawer && onOpenPricingDrawer(row.sku)}
          title="Click to open pricing drawer"
        >
          <span className="font-semibold text-orange-600 dark:text-orange-400">
            ${Number(price).toFixed(2)}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400 dark:border-orange-500/30">
            C
          </span>
        </div>
      );
    }
  },
  {
    id: 'pricing.channel',
    label: 'CHANNEL',
    section: SECTIONS.PRICING,
    accessor: (row) => row.pricing_tier || 'both',
    type: COLUMN_TYPES.BADGE,
    width: 120,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'product.read'],
    tooltip: 'Sales channel: Wholesale (B2B), Retail (B2C), or Both',
    formatter: (value, row) => {
      const tier = value || 'both';
      
      const tierConfig = {
        'wholesale': { 
          label: 'Wholesale', 
          color: 'blue', 
          icon: '🏢',
          className: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 dark:border-blue-500/30'
        },
        'retail': { 
          label: 'Retail', 
          color: 'green', 
          icon: '🛒',
          className: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/30'
        },
        'both': { 
          label: 'Both', 
          color: 'purple', 
          icon: '🔄',
          className: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400 dark:border-purple-500/30'
        }
      };
      
      const config = tierConfig[tier];
      
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config.className}`}>
          <span>{config.icon}</span>
          {config.label}
        </span>
      );
    }
  },

  {
    id: 'pricing.mode',
    label: 'Mode',
    section: SECTIONS.PRICING,
    accessor: (row) => row.pricing_mode,
    type: COLUMN_TYPES.BADGE,
    width: 100,
    editable: false,
    rbac: ['finance.read', 'product.read'],
    tooltip: 'Pricing calculation mode: System (auto) or Manual (override)',
    formatter: (value, row) => {
      const isSystem = value === 'system' || !value;
      const bgColor = isSystem ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800';
      const icon = isSystem ? '⚙️' : '📝';
      
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}>
          <span className="text-xs">{icon}</span>
          {isSystem ? 'System' : 'Manual'}
        </span>
      );
    }
  },

  // === PRICING V2 COLUMNS (Bulk Upload Integration) ===
  {
    id: 'pricingV2.wholesalePrice',
    label: 'WHOLESALE PRICE (V2)',
    section: SECTIONS.PRICING,
    accessor: (row) => safeAccess(row, 'pricing_v2.wholesale_price', () => row.pricing_v2?.wholesale_price),
    type: COLUMN_TYPES.MONEY,
    width: 150,
    sortable: true,
    editable: true,
    rbac: ['finance.edit', 'ownerops.edit'],
    tooltip: 'Wholesale/B2B price (calculated or direct from CSV)',
    headerClass: 'text-blue-400', // Blue for wholesale columns
    formatter: (value, row) => {
      const price = value !== undefined && value !== null ? value : null;
      if (price === null || price === 0) {
        return <span className="text-gray-400">-</span>;
      }
      return <span className="font-semibold text-blue-600 dark:text-blue-400">${Number(price).toFixed(2)}</span>;
    }
  },
  {
    id: 'pricingV2.retailPrice',
    label: 'RETAIL PRICE (V2)',
    section: SECTIONS.PRICING,
    accessor: (row) => safeAccess(row, 'pricing_v2.retail_price', () => row.pricing_v2?.retail_price),
    type: COLUMN_TYPES.MONEY,
    width: 150,
    sortable: true,
    editable: true,
    rbac: ['finance.edit', 'ownerops.edit'],
    tooltip: 'Retail/DTC price (calculated or direct from CSV)',
    headerClass: 'text-green-400', // Green for retail columns
    formatter: (value, row) => {
      const price = value !== undefined && value !== null ? value : null;
      if (price === null || price === 0) {
        return <span className="text-gray-400">-</span>;
      }
      return <span className="font-semibold text-green-600 dark:text-green-400">${Number(price).toFixed(2)}</span>;
    }
  },
  {
    id: 'pricingV2.wholesaleMargin',
    label: 'WHOLESALE MARGIN % (V2)',
    section: SECTIONS.PRICING,
    accessor: (row) => safeAccess(row, 'pricing_v2.wholesale_margin', () => row.pricing_v2?.wholesale_margin),
    type: COLUMN_TYPES.PERCENT,
    width: 180,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'product.read'],
    tooltip: 'Actual wholesale margin from pricing_v2 calculation',
    headerClass: 'text-blue-400', // Blue for wholesale columns
    formatter: (value, row) => {
      const margin = value !== undefined && value !== null ? value : null;
      if (margin === null) {
        return <span className="text-gray-400">-</span>;
      }
      // Color coding: green ≥30%, yellow 20-29%, red <20%
      let colorClass = 'text-red-600 dark:text-red-400';
      if (margin >= 30) colorClass = 'text-green-600 dark:text-green-400';
      else if (margin >= 20) colorClass = 'text-yellow-600 dark:text-yellow-400';
      
      return <span className={`font-semibold ${colorClass}`}>{Number(margin).toFixed(1)}%</span>;
    }
  },
  {
    id: 'pricingV2.retailMargin',
    label: 'RETAIL MARGIN % (V2)',
    section: SECTIONS.PRICING,
    accessor: (row) => safeAccess(row, 'pricing_v2.retail_margin', () => row.pricing_v2?.retail_margin),
    type: COLUMN_TYPES.PERCENT,
    width: 180,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'product.read'],
    tooltip: 'Actual retail margin from pricing_v2 calculation',
    headerClass: 'text-green-400', // Green for retail columns
    formatter: (value, row) => {
      const margin = value !== undefined && value !== null ? value : null;
      if (margin === null) {
        return <span className="text-gray-400">-</span>;
      }
      // Color coding: green ≥40%, yellow 30-39%, red <30%
      let colorClass = 'text-red-600 dark:text-red-400';
      if (margin >= 40) colorClass = 'text-green-600 dark:text-green-400';
      else if (margin >= 30) colorClass = 'text-yellow-600 dark:text-yellow-400';
      
      return <span className={`font-semibold ${colorClass}`}>{Number(margin).toFixed(1)}%</span>;
    }
  },
  {
    id: 'pricingV2.manufacturingCost',
    label: 'MANUFACTURING COST (V2)',
    section: SECTIONS.PRICING,
    accessor: (row) => safeAccess(row, 'pricing_v2.manufacturing_cost', () => row.pricing_v2?.manufacturing_cost),
    type: COLUMN_TYPES.MONEY,
    width: 160,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'ownerops.read'],
    tooltip: 'Aggregated manufacturing cost (includes raw materials, labor, factory overhead)',
    headerClass: 'text-orange-400', // Orange lighter for cost breakdown columns
    formatter: (value) => value != null ? `$${Number(value).toFixed(2)}` : <span className="text-gray-400">-</span>
  },
  {
    id: 'pricingV2.importShippingCost',
    label: 'IMPORT & SHIPPING (V2)',
    section: SECTIONS.PRICING,
    accessor: (row) => safeAccess(row, 'pricing_v2.import_shipping_cost', () => row.pricing_v2?.import_shipping_cost),
    type: COLUMN_TYPES.MONEY,
    width: 160,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'ownerops.read'],
    tooltip: 'Aggregated import & shipping cost (includes freight, duties, handling)',
    headerClass: 'text-orange-400', // Orange lighter for cost breakdown columns
    formatter: (value) => value != null ? `$${Number(value).toFixed(2)}` : <span className="text-gray-400">-</span>
  },
  {
    id: 'pricingV2.fulfillmentCost',
    label: 'FULFILLMENT COST (V2)',
    section: SECTIONS.PRICING,
    accessor: (row) => safeAccess(row, 'pricing_v2.fulfillment_cost', () => row.pricing_v2?.fulfillment_cost),
    type: COLUMN_TYPES.MONEY,
    width: 160,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'ownerops.read'],
    tooltip: 'Aggregated fulfillment cost (includes fulfillment + overhead allocation)',
    headerClass: 'text-orange-400', // Orange lighter for cost breakdown columns
    formatter: (value) => value != null ? `$${Number(value).toFixed(2)}` : <span className="text-gray-400">-</span>
  },
  {
    id: 'pricingV2.marketingPercent',
    label: 'MARKETING % (V2)',
    section: SECTIONS.PRICING,
    accessor: (row) => safeAccess(row, 'pricing_v2.marketing_percent', () => row.pricing_v2?.marketing_percent),
    type: COLUMN_TYPES.PERCENT,
    width: 140,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'ownerops.read'],
    tooltip: 'Marketing spend as percentage of retail price (ad spend allocation)',
    headerClass: 'text-orange-400', // Orange lighter for cost breakdown columns
    formatter: (value) => value != null ? `${Number(value).toFixed(1)}%` : <span className="text-gray-400">-</span>
  },
  {
    id: 'pricingV2.totalCost',
    label: 'TOTAL COGS (V2)',
    section: SECTIONS.PRICING,
    accessor: (row) => safeAccess(row, 'pricing_v2.total_cost', () => row.pricing_v2?.total_cost),
    type: COLUMN_TYPES.MONEY,
    width: 140,
    sortable: true,
    editable: false,
    rbac: ['finance.read', 'ownerops.read'],
    tooltip: 'Total Cost of Goods Sold - includes manufacturing, import/shipping, fulfillment, and marketing costs',
    headerClass: 'text-orange-500 font-semibold', // Orange PROMINENT for Total COGS
    formatter: (value) => value != null ? <span className="font-bold text-orange-600 dark:text-orange-400">${Number(value).toFixed(2)}</span> : <span className="text-gray-400">-</span>
  }
];

// Predefined Layout Variants (Simplified Presets)
export const DEFAULT_VARIANTS = {
  ALL: {
    name: 'All',
    description: 'Complete view with all essential columns',
    columns: [
      'general.sku', 'general.name', 'general.category', 'general.status',
      'plan.onHand', 'general.productType',
      'pricing.wholesalePrice', 'pricing.wholesaleMargin',
      'pricing.retailPrice', 'pricing.totalCost'
    ],
    sort: [{ id: 'general.sku', desc: false }],
    filters: []
  },
  PRICING: {
    name: 'Pricing',
    description: 'Focused pricing view with wholesale, retail, cost, and margins',
    columns: [
      'general.sku', 'general.name', 'general.category', 'general.productType',
      'pricingV2.rawMaterialCost', 'pricing.totalCost',
      'pricing.wholesalePrice', 'pricing.retailPrice',
      'pricing.wholesaleMargin', 'pricing.retailMargin'
    ],
    sort: [{ id: 'pricing.retailMargin', desc: true }],
    filters: []
  },
  INVENTORY: {
    name: 'Inventory',
    description: 'Inventory management view with stock levels and reorder status',
    columns: [
      'general.sku', 'general.name', 'general.category', 'general.status',
      'plan.onHand', 'inventory.fbaStock', 'inventory.fbmStock', 'inventory.awdStock',
      'plan.reorderPoint', 'sales.sellingUom'
    ],
    sort: [{ id: 'plan.onHand', desc: false }], // Show low stock first
    filters: []
  },
  COMPACT: {
    name: 'Compact',
    description: 'Minimal view with essential columns only',
    columns: [
      'general.sku', 'general.name', 'general.status', 'general.category',
      'pricing.retailPrice'
    ],
    sort: [{ id: 'general.sku', desc: false }],
    filters: []
  }
};

// Helper functions for column management
export const getColumnById = (id) => PRODUCT_COLUMN_CATALOG.find(col => col.id === id);

export const getColumnsBySection = (section) => PRODUCT_COLUMN_CATALOG.filter(col => col.section === section);

// RBAC filtering function (Phase 3: Enhanced)
export const filterColumnsByRBAC = (columns, userRoles = []) => {
  if (!userRoles || userRoles.length === 0) {
    // Return at least General fields for no-role users (fallback)
    return columns.filter(col => col.section === 'General').slice(0, 5);
  }
  
  return columns.filter(column => {
    const fieldCategory = getFieldCategory(column.id);
    return canViewField(userRoles, fieldCategory);
  });
};

export const getDefaultColumns = () => PRODUCT_COLUMN_CATALOG.filter(col => !col.hiddenByDefault);

export default PRODUCT_COLUMN_CATALOG;